import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiArrowSquareOutLight, PiLockSimpleLight, PiPaperPlaneTiltLight, PiShieldCheckLight, PiProhibitLight } from 'react-icons/pi';
import { showModal } from '../store/actions/modalActions';
import { useAppDispatch } from '../store';
import { useOrgDefaultCurrency } from '../hooks/useOrgDefaultCurrency';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';
import DetailFrame from '../components/shared/Frames/DetailFrame';
import GlassCard from '../components/shared/brand/GlassCard';
import { InfoRow } from '../components/shared/brand/Field';
import Button from '../components/shared/TailwindComponents/Button';
import Input from '../components/shared/TailwindComponents/Input';
import Textarea from '../components/shared/TailwindComponents/Textarea';
import Dropdown from '../components/shared/TailwindComponents/Dropdown';
import Skeleton from '../components/shared/TailwindComponents/Skeleton';
import EmptyState from '../components/shared/TailwindComponents/EmptyState';
import MarketplaceGate from '../components/Marketplace/MarketplaceGate';
import TriageSummary from '../components/Marketplace/TriageSummary';
import EngagementConversation from '../components/Marketplace/EngagementConversation';
import EngagementDocuments from '../components/Marketplace/EngagementDocuments';
import EngagementLetter from '../components/Marketplace/EngagementLetter';
import { LawyerProposalActions, ProposalHistory, isLive } from '../components/Marketplace/ProposalNegotiation';
import { enumLabel } from '../components/Marketplace/marketplaceLabels';
import {
  LeadStatusBadge,
  ProposalStatusBadge,
  Section,
  fmtDate,
  amountLabel,
  fmtProposalPrice,
  leadTitle,
  PROPOSAL_CURRENCIES,
} from '../components/Marketplace/marketplaceUi';
import {
  FEE_BASES,
  FeeBasis,
  Lead,
  Proposal,
  createProposal,
  errorMessage,
  errorStatus,
  getLead,
  EngagementView,
  declareConflict,
  getEngagement,
  getMyLawyerProfile,
  listProposals,
} from '../services/api/marketplaceService';

const AMOUNT_RE = /^\d+(\.\d{1,2})?$/;


/** Propose form for one (redacted) lead. 409 = this lawyer already proposed. */
const ProposalForm: React.FC<{ lead: Lead; onProposed: (p: Proposal | null) => void }> = ({ lead, onProposed }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [feeBasis, setFeeBasis] = useState<FeeBasis>('fixed');
  const [amount, setAmount] = useState('');
  // The lawyer bills in their firm's currency (the same default a new matter
  // takes); EUR only when the firm has none set.
  const { defaultCurrency } = useOrgDefaultCurrency();
  const [currency, setCurrency] = useState('');
  const [currencyTouched, setCurrencyTouched] = useState(false);
  useEffect(() => {
    if (!currencyTouched) setCurrency((defaultCurrency || 'EUR').toUpperCase());
  }, [defaultCurrency, currencyTouched]);
  const [planSummary, setPlanSummary] = useState('');
  const [terms, setTerms] = useState('');
  const [sending, setSending] = useState(false);

  const amountValid = AMOUNT_RE.test(amount.trim());
  const canSend = amountValid && planSummary.trim().length > 0 && /^[A-Za-z]{3}$/.test(currency.trim());

  const send = async () => {
    setSending(true);
    try {
      const proposal = await createProposal(lead.uuid, {
        fee_basis: feeBasis,
        amount: amount.trim(),
        currency: currency.trim().toUpperCase(),
        plan_summary: planSummary.trim(),
        ...(terms.trim() ? { terms: terms.trim() } : {}),
      });
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 4000, message: t('Proposal sent') }));
      onProposed(proposal);
    } catch (err) {
      if (errorStatus(err) === 409) {
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'info',
          duration: 4000,
          message: t('You have already sent a proposal for this request.'),
        }));
        onProposed(null);
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'error',
          duration: 4000,
          message: errorMessage(err, t('Could not send the proposal')),
        }));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <GlassCard icon={<PiPaperPlaneTiltLight />} title={t('Send a proposal')}
      subtitle={t('Price and plan. The client compares proposals and accepts one.')}>
      <div className="space-y-4" data-testid="marketplace-proposal-form">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Dropdown
            id="marketplace-fee-basis"
            label={t('Fee basis')}
            options={FEE_BASES.map((b) => ({ value: b, label: enumLabel(t, b) }))}
            value={feeBasis}
            onChange={(v) => setFeeBasis(v as FeeBasis)}
            fullWidth
          />
          <Input
            type="text"
            id="marketplace-amount"
            label={amountLabel(t, feeBasis)}
            placeholder="1500.00"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={amount && !amountValid ? t('Enter an amount like 1500.00') : undefined}
            required
          />
          <Dropdown
            id="marketplace-currency"
            label={t('Currency')}
            options={Array.from(new Set([currency, ...PROPOSAL_CURRENCIES].filter(Boolean)))
              .map((c) => ({ value: c, label: c }))}
            value={currency}
            onChange={(v) => { setCurrencyTouched(true); setCurrency(String(v)); }}
            fullWidth
          />
        </div>
        <Textarea
          id="marketplace-plan"
          label={t('Plan summary')}
          placeholder={t('How you would handle this matter')}
          value={planSummary}
          onChange={(e) => setPlanSummary(e.target.value)}
          required
        />
        <Textarea
          id="marketplace-terms"
          label={t('Terms (optional)')}
          value={terms}
          rows={3}
          onChange={(e) => setTerms(e.target.value)}
        />
        <div className="flex justify-end border-t border-hairline-soft pt-4">
          <Button variant="primary" size="small" onClick={send} isLoading={sending} disabled={sending || !canSend}
            icon={<PiPaperPlaneTiltLight className="h-3.5 w-3.5" />}>
            {t('Send proposal')}
          </Button>
        </div>
      </div>
    </GlassCard>
  );
};

const SentProposal: React.FC<{ proposal: Proposal; canNegotiate: boolean; onChanged: (p: Proposal) => void }> = ({
  proposal, canNegotiate, onChanged,
}) => {
  const { t } = useTranslation();
  return (
    <GlassCard icon={<PiPaperPlaneTiltLight />} title={t('Proposal sent')} actions={<ProposalStatusBadge status={proposal.status} />}
      subtitle={proposal.status === 'countered' ? t('The client asked for changes. Revise your proposal, or leave it as it is.') : undefined}
      tone={proposal.status === 'countered' ? 'warning' : undefined}>
      <div className="space-y-4" data-testid="marketplace-sent-proposal">
        <div className="space-y-2">
          <InfoRow label={t('Fee basis')} value={enumLabel(t, proposal.fee_basis)} />
          <InfoRow label={amountLabel(t, proposal.fee_basis)} value={fmtProposalPrice(t, proposal)} />
          <InfoRow label={t('Sent')} value={fmtDate(proposal.created_at)} />
        </div>
        <p className="whitespace-pre-wrap border-t border-hairline-soft pt-3 text-sm leading-relaxed text-fg-body">
          {proposal.plan_summary}
        </p>
        {proposal.terms && <p className="whitespace-pre-wrap text-[11px] text-fg-muted">{proposal.terms}</p>}
        <ProposalHistory proposal={proposal} viewer="lawyer" />
        {canNegotiate && isLive(proposal) && <LawyerProposalActions proposal={proposal} onChanged={onChanged} />}
      </div>
    </GlassCard>
  );
};

/** Lawyer side: one inbox request - the redacted triage, and my proposal. */
const InboxDetailContent: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  // A lawyer who can't take the request (e.g. their firm acts for the other
  // side) steps aside; they won't see it again. No reason is asked or stored.
  const confirmConflict = () => dispatch(showModal({
    showModal: true,
    type: 'confirmation',
    title: t('Conflict of interest'),
    message: t("You can't take this request?"),
    subText: t("It disappears from your inbox and you won't be suggested to this client."),
    icon: <PiProhibitLight className="w-6 h-6 text-danger-600" />,
    variant: 'danger',
    confirmLabel: t('Step aside'),
    onConfirm: async () => {
      try {
        await declareConflict(leadUuid);
        dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 4000, message: t('Removed from your inbox') }));
        history.push('/marketplace/inbox');
      } catch (err) {
        dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: errorMessage(err, t('Could not save')) }));
      }
    },
  }));
  const { leadUuid } = useParams<{ leadUuid: string }>();
  const [lead, setLead] = useState<Lead | null | undefined>(undefined);
  const [myProposal, setMyProposal] = useState<Proposal | null | undefined>(undefined);
  // Only a verified lawyer may propose (the backend refuses otherwise).
  const [verified, setVerified] = useState<boolean | undefined>(undefined);
  // Once hired: the conversation with the client (403 for any other lawyer).
  const [engagement, setEngagement] = useState<EngagementView | null>(null);
  // Bumped when the letter is issued, so the documents list shows it.
  const [docsVersion, setDocsVersion] = useState(0);
  const engagedHere = lead?.status === 'engaged' && myProposal?.status === 'accepted';
  useEffect(() => {
    if (!engagedHere) return;
    getEngagement(leadUuid, 'lawyer').then(setEngagement).catch(() => setEngagement(null));
  }, [engagedHere, leadUuid]);

  const load = useCallback(async () => {
    try {
      const [l, mine, profile] = await Promise.all([
        getLead(leadUuid), listProposals(leadUuid), getMyLawyerProfile().catch(() => null),
      ]);
      setLead(l);
      setVerified(profile?.verification_status === 'verified' || profile?.verification_status === 'firm_backed');
      // Lawyer view returns only this lawyer's own proposal (if any).
      setMyProposal(mine[0] ?? null);
    } catch {
      setLead(null);
    }
  }, [leadUuid]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <DetailFrame
      breadcrumbs={[
        { name: t('Marketplace'), href: '/marketplace', current: false },
        { name: t('Lawyer inbox'), href: '/marketplace/inbox', current: false },
        { name: lead ? leadTitle(t, lead) : t('Request'), href: `/marketplace/inbox/${leadUuid}`, current: true },
      ]}
      onBack={() => history.push('/marketplace/inbox')}
      actions={
        lead?.matter_uuid
          ? [{ label: t('Open matter'), onClick: () => history.push(`/matters/${lead.matter_uuid}`), icon: <PiArrowSquareOutLight className="w-4 h-4" />, variant: 'primary' as const }]
          : []
      }
    >
      <div className="details-canvas h-full w-full overflow-y-auto custom-scrollbar-main" data-testid="marketplace-inbox-detail">
        {lead === undefined ? (
          <Skeleton variant="detail" />
        ) : lead === null ? (
          <EmptyState headline={t('Request not available')} subtext={t('It may have been engaged by another lawyer or closed.')} />
        ) : (
          <div className="details-pad w-full space-y-7 px-6 py-6">
            {lead.redacted ? (
              <p className="flex items-start gap-1.5 text-[11px] text-fg-muted">
                <PiLockSimpleLight className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{t("You see the triage summary only. The client's description and documents unlock after engagement.")}</span>
              </p>
            ) : (
              // Engaged: progressive disclosure has unlocked the client's own words.
              <Section title={t("Client's request")}>
                <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-fg-body" data-testid="marketplace-client-request">
                  {lead.issue_text}
                </p>
              </Section>
            )}
            <div className="details-body items-start gap-6">
              <div className="min-w-0 space-y-7">
                {engagedHere && engagement && (
                  <Section title={t('Engagement letter')}>
                    <EngagementLetter leadUuid={leadUuid} as="lawyer" onIssued={() => setDocsVersion((v) => v + 1)} />
                  </Section>
                )}
                {engagedHere && engagement && (
                  <Section title={t('Messages with your client')}>
                    <EngagementConversation leadUuid={leadUuid} as="lawyer" canMessage={engagement.can_message}
                      otherName={engagement.client?.display_name} />
                  </Section>
                )}
                {engagedHere && engagement && (
                  <Section title={t('Documents shared with your client')}>
                    <EngagementDocuments key={docsVersion} leadUuid={leadUuid} as="lawyer" canAdd={engagement.can_message} />
                  </Section>
                )}
                <Section title={t('Your proposal')}>
                  {myProposal === undefined || verified === undefined ? (
                    <Skeleton variant="form" />
                  ) : myProposal ? (
                    <SentProposal proposal={myProposal} canNegotiate={lead.status !== 'engaged'}
                      onChanged={(p) => { setMyProposal(p); load(); }} />
                  ) : !verified ? (
                    <GlassCard icon={<PiShieldCheckLight />} title={t('Verify your profile to send proposals')}
                      subtitle={t('Confirm your identity with BankID and your firm, and PONS checks your licence.')}>
                      <div className="flex justify-end" data-testid="marketplace-verify-first">
                        <Button variant="primary" size="small" onClick={() => history.push('/marketplace/inbox')}>
                          {t('Go to verification')}
                        </Button>
                      </div>
                    </GlassCard>
                  ) : (
                    <ProposalForm lead={lead} onProposed={(p) => {
                      if (p) setMyProposal(p);
                      // The lead is 'proposed' now; don't show 'triaged' until the reload lands.
                      setLead((l) => (l ? { ...l, status: 'proposed' } : l));
                      load();
                    }} />
                  )}
                </Section>
                {lead.redacted && !myProposal && (
                  <div className="flex justify-end">
                    <button type="button" onClick={confirmConflict} data-testid="marketplace-declare-conflict"
                      className="text-[11px] text-fg-muted underline-offset-2 hover:text-fg-body hover:underline">
                      {t('I have a conflict of interest')}
                    </button>
                  </div>
                )}
              </div>
              <div className="details-side min-w-0 space-y-7">
                {lead.triage && (
                  <Section title={t('Triage')}>
                    <TriageSummary triage={lead.triage} audience="lawyer" />
                  </Section>
                )}
                <Section title={t('Details')}>
                  <GlassCard>
                    <div className="space-y-2">
                      <InfoRow label={t('Status')}><LeadStatusBadge status={lead.status} audience="lawyer" /></InfoRow>
                      <InfoRow label={t('Received')} value={fmtDate(lead.created_at)} />
                      <InfoRow label={t('Documents')} value={String(lead.document_count)} />
                    </div>
                  </GlassCard>
                </Section>
              </div>
            </div>
          </div>
        )}
      </div>
    </DetailFrame>
  );
};

const MarketplaceInboxDetailPage: React.FC = () => (
  <MarketplaceGate skeleton="detail">{() => <InboxDetailContent />}</MarketplaceGate>
);

export default MarketplaceInboxDetailPage;
